import Skeleton from './Skeleton';
import { useTranslation } from '../presentation/common/useTranslation.js';
import FeatureIcon from '../presentation/common/FeatureIcon.jsx';
import HeroClass from './HeroClass';
import HeroRank from './HeroRank';
import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';

import { QRCodeSVG } from 'qrcode.react';
import { ClassIcon } from './GameIcon';
import { Maximize2, X, Sparkles, Medal } from 'lucide-react';
import { getAdventurerQrPass } from '../services/api/partner';
import { karmaIconColor } from '../utils/karmaIconColor';
import KarmaScoreLink from './KarmaScoreLink';
import { getKarmaTier } from './KarmaBarometer';
import { categoryTitle, parseCategoryTitle } from '../utils/categoryMilestones';

export default function AdventurerQrPass({ 
  username, 
  heroClass, 
  rank, 
  compact = false, 
  hideInline = false, 
  isOpen, 
  onClose, 
  avatarUrl,
  karmaScore = 0,
  karmaMinimum = -30,
  approvedActions = 0,
  karmaTitle,
  selectedCategoryTitle,
  karmaRules,
  isBanned,
  karmaSuspendedUntil
}) {
  const { t, i18n } = useTranslation();
  const selectedTitle = parseCategoryTitle(selectedCategoryTitle);
  const selectedTitleLabel = selectedTitle ? categoryTitle(selectedTitle.categoryKey, selectedTitle.level, i18n.language) : '';
  const karmaTier = getKarmaTier(karmaScore, isBanned, karmaSuspendedUntil, karmaRules);
  const karmaTitleLabel = karmaTitle || (karmaTier?.labelKey ? t(karmaTier.labelKey) : '');
  const [passData, setPassData] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  const [loading, setLoading] = useState(true);
  const [localIsOpen, setLocalIsOpen] = useState(false);

  const isFetchingRef = useRef(false);
  const lastFetchRef = useRef(0);

  const isModalOpen = isOpen !== undefined ? isOpen : localIsOpen;
  const setIsModalOpenState = (val) => {
    if (onClose && !val) {
      onClose();
    }
    setLocalIsOpen(val);
  };

  const fetchPass = useCallback(async (isManual = false) => {
    if (isFetchingRef.current) return;
    if (!isManual && Date.now() - lastFetchRef.current < 3000) return;
    isFetchingRef.current = true;
    lastFetchRef.current = Date.now();
    setLoading(true);
    try {
      const data = await getAdventurerQrPass();
      if (data?.qrToken && new Date(data.expiresAt).getTime() > Date.now()) {
        setPassData(data);
      } else { setPassData(null); }
    } catch {
      setPassData(null);
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, []);

  useEffect(() => {
    fetchPass();
  }, [fetchPass]);

  // Auto-refresh pass token before expiration
  useEffect(() => {


    const interval = setInterval(() => {
      setNow(Date.now());
      const expTime = new Date(passData?.expiresAt || 0).getTime();
      const now = Date.now();
      const diff = Math.max(0, Math.floor((expTime - now) / 1000));

      // Auto-refresh when pass expires or is under 4 seconds
      if (diff <= 4 && Date.now() - lastFetchRef.current >= 15000) {
        fetchPass();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [passData, fetchPass]);

  const displayToken = new Date(passData?.expiresAt).getTime() > now ? passData?.qrToken : null;
  if (!displayToken) {
    if (hideInline && !isModalOpen) return null;
    return <div role="status" className="world-panel p-3 text-center">
      <p>{t(loading ? 'qr_pass.loading' : 'qr_pass.unavailable')}</p>
      {!loading && <button type="button" className="world-button" onClick={() => fetchPass(true)}>{t('qr_pass.retry')}</button>}
      {isModalOpen && <button type="button" className="world-button" onClick={() => setIsModalOpenState(false)}>{t('common.close')}</button>}
    </div>;
  }

  // Hidden inline view - only renders the modal if open
  if (hideInline) {
    return isModalOpen ? (
      <QrModal 
        token={displayToken} 
        onClose={() => setIsModalOpenState(false)}
        username={username}
        heroClass={heroClass}
        rank={rank}
        avatarUrl={avatarUrl}
        karmaScore={karmaScore}
        karmaMinimum={karmaMinimum}
        approvedActions={approvedActions}
        karmaTitle={karmaTitleLabel}
        selectedTitle={selectedTitleLabel}
      />
    ) : null;
  }

  // Mini / Compact View for inside small card sections
  if (compact) {
    return (
      <div className="bg-zinc-950 border border-zinc-800 p-3 flex items-center justify-center">
        <div 
          onClick={() => setIsModalOpenState(true)}
          className="w-12 h-12 bg-white p-1 cursor-pointer border border-zinc-700 hover:scale-105 transition-transform flex items-center justify-center shrink-0"
          title={t('qr_pass.click_to_enlarge', 'Klicken zum Vergrößern')}
        >
          {loading ? (
            <Skeleton className="w-full h-full" />
          ) : (
            <QRCodeSVG 
              value={displayToken} 
              size={40} 
              level="M" 
              fgColor="#09090b" 
              bgColor="#ffffff" 
            />
          )}
        </div>

        {/* Modal */}
        {isModalOpen && (
          <QrModal 
            token={displayToken} 
            onClose={() => setIsModalOpenState(false)}
            username={username}
            heroClass={heroClass}
            rank={rank}
            avatarUrl={avatarUrl}
            karmaScore={karmaScore}
            karmaMinimum={karmaMinimum}
            approvedActions={approvedActions}
            karmaTitle={karmaTitleLabel}
            selectedTitle={selectedTitleLabel}
          />
        )}
      </div>
    );
  }

  // QR Code Only View
  return (
    <>
      <div 
        onClick={() => setIsModalOpenState(true)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setIsModalOpenState(true); }}
        className="relative w-20 h-20 sm:w-[88px] sm:h-[88px] bg-white p-1.5 rounded-xl border border-amber-400/50 shadow-md hover:shadow-lg hover:shadow-amber-500/20 hover:border-amber-400 transition-all duration-200 cursor-pointer group flex items-center justify-center shrink-0 overflow-hidden select-none hover:scale-105"
        title={t('qr_pass.click_to_enlarge', 'Klicken zum Vergrößern')}
      >
        {loading ? (
          <Skeleton className="w-full h-full rounded-lg" />
        ) : (
          <QRCodeSVG 
            value={displayToken} 
            size={76} 
            level="M" 
            fgColor="#09090b" 
            bgColor="#ffffff" 
            className="w-full h-full"
          />
        )}

        {/* Hover Enlarge Overlay */}
        <div className="absolute inset-0 bg-black/65 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center text-white text-[9px] font-bold uppercase tracking-wider gap-0.5 pointer-events-none rounded-xl">
          <Maximize2 className="w-4 h-4 text-amber-300" />
          <span>{t('qr_pass.enlarge_btn', 'Vergrößern')}</span>
        </div>
      </div>

      {/* Enlarged Scanning Modal */}
      {isModalOpen && (
        <QrModal 
          token={displayToken} 
          onClose={() => setIsModalOpenState(false)}
          username={username}
          heroClass={heroClass}
          rank={rank}
          avatarUrl={avatarUrl}
          karmaScore={karmaScore}
          karmaMinimum={karmaMinimum}
          approvedActions={approvedActions}
          karmaTitle={karmaTitleLabel}
          selectedTitle={selectedTitleLabel}
        />
      )}
    </>
  );
}

function QrModal({ 
  token, 
  onClose, 
  username,
  heroClass,
  rank,
  avatarUrl,
  karmaScore = 0,
  karmaMinimum = -30,
  approvedActions = 0,
  karmaTitle,
  selectedTitle
}) {
  const { t } = useTranslation();

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return createPortal(
    <div 
      className="world-modal-overlay fixed inset-0 z-[99999] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={(e) => {
        e.stopPropagation();
        onClose();
      }}
    >
      <div 
        className="qr-pass-modal bg-[#081521ed] border border-[#b99b6070] max-w-sm w-full p-5 rounded-2xl shadow-2xl space-y-4 relative animate-in fade-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        
        {/* Close Button */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          className="qr-modal-close absolute top-3.5 right-3.5 text-zinc-400 hover:text-white p-1.5 rounded-lg hover:bg-zinc-800/80 transition-colors"
          aria-label={t('common.close', 'Schließen')}
        >
          <X className="w-4 h-4" />
        </button>

        {/* Modal Header & Hero Info */}
        <div className="text-center flex flex-col items-center">
          {avatarUrl && (
            <img 
              src={avatarUrl} 
              alt="" 
              className="w-16 h-16 rounded-full border-2 border-[#d9b56d] object-cover shadow-md mb-2.5" 
            />
          )}

          <div className="qr-modal-badge inline-flex items-center gap-1.5 px-3 py-0.5 rounded-full bg-[#152737] border border-[#ae925255] text-amber-200 text-[10px] font-mono font-bold uppercase tracking-wider">
            <Sparkles className="w-3 h-3 text-amber-400" />
            <span className="qr-modal-badge-label">{t('qr_pass.modal_badge', 'Offizieller Gilden-Ausweis')}</span>
          </div>

          <h2 className="qr-modal-username font-serif text-xl font-bold tracking-wide text-[#fff0ce] mt-2">
            {username || t('common.adventurer')}
          </h2>

          {(karmaTitle || selectedTitle) && (
            <div className="qr-modal-titles mt-1 flex flex-col items-center gap-0.5 text-center">
              {karmaTitle && <span className="qr-modal-karma-title text-xs text-[#c4b5fd]">{karmaTitle}</span>}
              {selectedTitle && <strong className="qr-modal-selected-title text-xs text-[#ebd5a7]">{selectedTitle}</strong>}
            </div>
          )}

          {/* Structured Class Badge */}
          <div className="qr-modal-class inline-flex items-center gap-2 px-3 py-1.5 mt-1.5 rounded-xl bg-[#0c1a26] border border-[#b99b6044] text-amber-100 shadow-inner">
            {heroClass && (
              <ClassIcon 
                classKey={heroClass} 
                alt="" 
                className="w-5 h-5 object-contain drop-shadow-[0_2px_8px_rgba(217,181,109,0.35)]" 
              />
            )}
            <span className="qr-modal-class-label font-serif text-xs font-bold text-[#eee9df]">
              <HeroClass heroClass={heroClass} fallback={heroClass || t('common.novice')} className="qr-modal-class-label" />
            </span>
          </div>

          {/* Structured Stats Strip with Dashboard System Icons */}
          <div className="qr-modal-stats grid grid-cols-3 gap-2 w-full mt-3.5 pt-3 border-t border-[#ae925233]">
            {/* Rank */}
            <div 
              className="qr-modal-stat bg-[#0b1a24]/85 border border-[#ae925233] rounded-xl py-2 px-1.5 flex flex-col items-center justify-center gap-1 text-center shadow-inner"
              title={t('dashboard_v2.rank', 'Rang')}
            >
              <HeroRank rank={rank || 'F'} showLabel={false} className="qr-modal-rank text-amber-300" />
            </div>

            {/* Karma */}
            <div 
              className="qr-modal-stat bg-[#0b1a24]/85 border border-[#ae925233] rounded-xl py-2 px-1.5 flex flex-col items-center justify-center gap-1 text-center shadow-inner"
              title={t('dashboard_v2.karma', 'Karma')}
            >
              <FeatureIcon name="engagement" className="w-5 h-5 text-emerald-400 shrink-0" style={karmaScore < 0 ? { color: karmaIconColor(karmaScore, karmaMinimum) } : undefined} aria-hidden="true" />
              <strong className="qr-karma-value font-serif text-base font-bold text-emerald-300 leading-none" data-negative={karmaScore < 0} style={karmaScore < 0 ? { '--negative-karma-color': karmaIconColor(karmaScore, karmaMinimum) } : undefined}>
                <KarmaScoreLink>{karmaScore >= 0 ? `+${karmaScore}` : karmaScore}</KarmaScoreLink>
              </strong>
            </div>

            {/* Gute Taten */}
            <div 
              className="qr-modal-stat bg-[#0b1a24]/85 border border-[#ae925233] rounded-xl py-2 px-1.5 flex flex-col items-center justify-center gap-1 text-center shadow-inner"
              title={t('hero_card.actions', 'Gute Taten')}
            >
              <Medal className="w-5 h-5 text-sky-400 shrink-0 drop-shadow-[0_0_8px_rgba(56,189,248,0.4)]" aria-hidden="true" />
              <strong className="qr-action-value font-serif text-base font-bold text-[#f2e9d9] leading-none">
                {approvedActions}
              </strong>
            </div>
          </div>
        </div>

        {/* Large High-Contrast QR Code */}
        <div className="qr-modal-code bg-white p-3.5 mx-auto w-fit rounded-xl border border-zinc-700 shadow-2xl flex items-center justify-center">
          <QRCodeSVG 
            value={token} 
            size={190} 
            level="H" 
            fgColor="#000000" 
            bgColor="#ffffff" 
          />
        </div>

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          className="qr-modal-close w-full py-2.5 bg-gradient-to-b from-[#d9b56d] to-[#b8954d] hover:from-[#e5c57d] hover:to-[#c4a159] text-zinc-950 font-bold uppercase tracking-wider text-xs rounded-xl transition-all shadow-md active:scale-[0.99]"
        >
          {t('qr_pass.close', 'Schließen')}
        </button>

      </div>
    </div>,
    document.body
  );
}
